import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { formatDateTimeCompact } from "@/utils/dateUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExportStatusBadge } from "@/components/common/data-export/ExportStatusBadge";
import { ExportJobStatusDialog } from "@/components/common/data-export/ExportJobStatusDialog";
import { GuestExportDialog } from "@/components/common/data-export/GuestExportDialog";
import { useExportJobs } from "@/hooks/useDataExport";
import {
  ExportJob,
  ExportJobStatus,
  ExportJobsQuery,
  ExportTargetType,
} from "@/types/data-export";

const PAGE_LIMIT = 20;
const ALL = "ALL";

const STATUS_OPTIONS = Object.values(ExportJobStatus);
const TARGET_TYPE_OPTIONS = Object.values(ExportTargetType);

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];
const TYPE_BADGE: Record<ExportTargetType, BadgeVariant> = {
  USER: "status_approved",
  GUEST: "status_done",
};

export const ExportsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);

  const statusFilter = (searchParams.get("status") ?? ALL) as
    | ExportJobStatus
    | typeof ALL;
  const targetTypeFilter = (searchParams.get("targetType") ?? ALL) as
    | ExportTargetType
    | typeof ALL;
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === ALL) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key !== "page") {
        next.delete("page");
      }
      return next;
    });
  };

  const query = useMemo<ExportJobsQuery>(
    () => ({
      page,
      limit: PAGE_LIMIT,
      ...(statusFilter !== ALL ? { status: statusFilter } : {}),
      ...(targetTypeFilter !== ALL ? { targetType: targetTypeFilter } : {}),
    }),
    [page, statusFilter, targetTypeFilter],
  );

  const { data, isLoading } = useExportJobs(query);
  const jobs = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_LIMIT));

  const openJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setStatusDialogOpen(true);
  };

  const columns: Column<ExportJob>[] = [
    {
      key: "createdAt",
      header: t("dataExport.audit.columns.created"),
      render: (value: string) => formatDateTimeCompact(value),
    },
    {
      key: "targetType",
      header: t("dataExport.audit.columns.type"),
      render: (_: unknown, row: ExportJob) => (
        <Badge variant={TYPE_BADGE[row.targetType]}>
          {t(`dataExport.targetType.${row.targetType}`)}
        </Badge>
      ),
    },
    {
      key: "targetId",
      header: t("dataExport.audit.columns.target"),
      render: (_: unknown, row: ExportJob) => (
        <span className="break-all font-mono text-xs text-muted-foreground">
          {row.targetLabel ?? row.targetId}
        </span>
      ),
    },
    {
      key: "status",
      header: t("dataExport.audit.columns.status"),
      render: (_: unknown, row: ExportJob) => (
        <ExportStatusBadge status={row.status} />
      ),
    },
    {
      key: "requestedByEmail",
      header: t("dataExport.audit.columns.requestedBy"),
      render: (value: string | null) => value ?? "—",
    },
    {
      key: "completedAt",
      header: t("dataExport.audit.columns.completed"),
      render: (value: string | null) => formatDateTimeCompact(value),
    },
    {
      key: "expiresAt",
      header: t("dataExport.audit.columns.expires"),
      render: (value: string | null) => formatDateTimeCompact(value),
    },
    {
      key: "actions",
      header: t("dataExport.audit.columns.actions"),
      className: "text-right",
      render: (_: unknown, row: ExportJob) => (
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            openJob(row.id);
          }}
        >
          {t("dataExport.audit.view")}
        </Button>
      ),
    },
  ];

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("dataExport.audit.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("dataExport.audit.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          variant="new_default"
          className="w-fit shrink-0"
          onClick={() => setGuestDialogOpen(true)}
        >
          <FileDown className="size-4" />
          {t("dataExport.audit.exportGuest")}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-2xl border bg-background p-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-none">
            {t("dataExport.audit.filters.status")}
          </span>
          <Select
            value={statusFilter}
            onValueChange={(value) => setParam("status", value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t("dataExport.audit.filters.all")}
              </SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`dataExport.status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-none">
            {t("dataExport.audit.filters.targetType")}
          </span>
          <Select
            value={targetTypeFilter}
            onValueChange={(value) => setParam("targetType", value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t("dataExport.audit.filters.all")}
              </SelectItem>
              {TARGET_TYPE_OPTIONS.map((targetType) => (
                <SelectItem key={targetType} value={targetType}>
                  {t(`dataExport.targetType.${targetType}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border bg-background p-6">
        <DataTable
          data={jobs}
          columns={columns}
          loading={isLoading}
          getRowKey={(row) => row.id}
          onRowClick={(row) => openJob(row.id)}
          pagination={{
            currentPage: page,
            totalPages,
            onPageChange: (next) => setParam("page", String(next)),
          }}
          emptyMessage={t("dataExport.audit.empty")}
        />
      </div>

      <ExportJobStatusDialog
        jobId={selectedJobId}
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
      />

      <GuestExportDialog
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
      />
    </div>
  );
};
